/**
 * Method recommendation overrides — the record of what the situation rules
 * recommended, what was chosen, and which rules that choice overrode.
 *
 * This is the feedback the authored rule table (situationRules.ts) is revised
 * against. A row is written for every method choice made while a situation
 * rule was in effect — including choices that agreed with the recommendation,
 * because "the rule was followed" is evidence too.
 *
 * Not an authority transition (§2.3a): a plain client INSERT under RLS
 * (migration 715). `created_by` defaults to auth.uid() in the database and the
 * INSERT policy binds it there, so attribution can never be caller-supplied.
 */

import type { Json } from '../types/database.generated';
import { SITUATION_VECTOR_VERSION, type SituationVector } from '../types/prioritization/situationVector';
import type { SituationRecommendation } from './situationRecommendation';
import { getTypedSupabaseClient } from './typedSupabaseClient';

export interface MethodOverrideRecord {
  workspaceId: string;
  sessionId: string | null;
  /** Shape-matrix tool id of what was chosen. */
  chosenToolId: string;
  /** Shape-matrix tool id the rules put first, or null when nothing was promoted. */
  recommendedToolId: string | null;
  /** Rule ids the choice went against: rules whose promotion was declined, or whose discouragement was ignored. */
  overriddenRuleIds: string[];
  situationVector: SituationVector | null;
  reason: string | null;
}

function unique(values: readonly string[]): string[] {
  return Array.from(new Set(values));
}

/**
 * Pure. What to record for a choice made in the presence of a recommendation.
 * Returns null when no rule fired — there is nothing to learn from a choice
 * the rules had no opinion on.
 */
export function buildMethodOverrideRecord(input: {
  workspaceId: string;
  sessionId: string | null;
  chosenToolId: string;
  recommendation: SituationRecommendation;
  vector: SituationVector | null | undefined;
  reason?: string | null;
}): MethodOverrideRecord | null {
  const { recommendation } = input;
  if (!recommendation.applied) return null;

  const promoted = recommendation.annotations.filter((a) => a.kind === 'promoted');
  const recommendedToolId = promoted[0]?.toolId ?? recommendation.orderedToolIds[0] ?? null;

  const declinedPromotions = promoted
    .filter((a) => a.toolId !== input.chosenToolId)
    .map((a) => a.ruleId);
  const ignoredDiscouragements = recommendation.annotations
    .filter((a) => a.kind === 'discouraged' && a.toolId === input.chosenToolId)
    .map((a) => a.ruleId);

  // A rule that promoted the chosen tool was followed, not overridden — even if
  // it also promoted something else.
  const followed = new Set(promoted.filter((a) => a.toolId === input.chosenToolId).map((a) => a.ruleId));

  return {
    workspaceId: input.workspaceId,
    sessionId: input.sessionId,
    chosenToolId: input.chosenToolId,
    recommendedToolId,
    overriddenRuleIds: unique([...declinedPromotions, ...ignoredDiscouragements]).filter((id) => !followed.has(id)),
    situationVector: input.vector ?? null,
    reason: input.reason ?? null,
  };
}

export async function recordMethodRecommendationOverride(
  record: MethodOverrideRecord,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { error } = await getTypedSupabaseClient()
    .from('method_recommendation_overrides')
    .insert({
      workspace_id: record.workspaceId,
      session_id: record.sessionId,
      chosen_tool_id: record.chosenToolId,
      recommended_tool_id: record.recommendedToolId,
      overridden_rule_ids: record.overriddenRuleIds,
      // The vector is a plain object of string enums; it is a valid Json value.
      situation_vector: (record.situationVector as Json | null) ?? null,
      situation_vector_version: record.situationVector ? SITUATION_VECTOR_VERSION : null,
      reason: record.reason,
    });
  if (error) return { ok: false, error: `recordMethodRecommendationOverride failed: ${error.message}` };
  return { ok: true };
}

/**
 * Convenience for UI call sites: build and record in one step, never throw.
 * A failure to record is logged and otherwise ignored — the choice itself has
 * already been made and persisted elsewhere.
 */
export async function recordMethodChoice(input: Parameters<typeof buildMethodOverrideRecord>[0]): Promise<void> {
  const record = buildMethodOverrideRecord(input);
  if (!record) return;
  const result = await recordMethodRecommendationOverride(record);
  if (!result.ok) console.warn('[methodRecommendationOverrides]', result.error);
}
